import { Button, CardSecondary, Text } from "@olivahealth/oli-ui";
import {
  graphql,
  PreloadedQuery,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";
import { useEffect, Suspense } from "react";
import { useRouter } from "next/router";
import { CenteredInfinityLoader } from "../../molecules/Loaders/InfinityLoader";
import Image from "../../../components/common/Image";
import CompanionSessionCard from "../../molecules/CompanionSessionCard";
import useTranslation from "../../../hooks/useTranslation";
import { CompanionSessionListWidgetQuery as IQuery } from "./__generated__/CompanionSessionListWidgetQuery.graphql";

import "swiper/css";

const CompanionSessionListWidgetQuery = graphql`
  query CompanionSessionListWidgetQuery {
    threads(first: 3, type: WELLBEING) {
      edges {
        node {
          id
          type
          ...CompanionSessionCard_thread
        }
      }
    }
  }
`;

interface Props {
  queryRef: PreloadedQuery<IQuery>;
}

export default function CompanionSessionListWidget() {
  const { t } = useTranslation("companion");
  const [queryRef, loadQuery] = useQueryLoader<IQuery>(
    CompanionSessionListWidgetQuery,
  );
  const { push } = useRouter();

  useEffect(() => {
    loadQuery({}, { fetchPolicy: "network-only" });
  }, [loadQuery]);

  if (queryRef == null) {
    return <CenteredInfinityLoader />;
  }

  const navigateToAllThreads = () => {
    push("/companion/threads");
  };

  return (
    <Suspense fallback={<CenteredInfinityLoader />}>
      <div>
        <span className="flex items-center justify-between mb-6">
          <Text weight="bold" size="xl" color="white">
            {t("companionSessionListWidget.title")}
          </Text>
          <Button variant="linkLight" onClick={navigateToAllThreads}>
            {t("companionSessionListWidget.seeAllCta")}
          </Button>
        </span>
        <CompanionSessionListWidgetContent queryRef={queryRef} />
      </div>
    </Suspense>
  );
}

function CompanionSessionListWidgetContent({ queryRef }: Props) {
  const data = usePreloadedQuery<IQuery>(
    CompanionSessionListWidgetQuery,
    queryRef,
  );
  const hasThreads = data.threads.edges.length > 0;

  const { push } = useRouter();

  const navigateToThread = (id: string) => {
    push(`/companion/threads/${id}`);
  };

  if (!hasThreads) {
    return <EmptyState />;
  }

  return (
    <div className="flex flex-col gap-3 w-full">
      {data.threads.edges.map((edge) => (
        <CompanionSessionCard
          variant="compact"
          key={edge.node.id}
          thread={edge.node}
          onClick={navigateToThread}
        />
      ))}
    </div>
  );
}

export function EmptyState() {
  const { t } = useTranslation("companion");

  return (
    <CardSecondary
      title={t("companionSessionListWidget.emptyState.title")}
      textColor="white"
      titleTextColor="white"
      image={
        <Image
          className="mb-3"
          src="/img/img_chat_bubbles.png"
          alt=""
          width={64}
          height={64}
        />
      }
      backgroundColorClassName="bg-neutral-800"
      description={t("companionSessionListWidget.emptyState.description")}
    />
  );
}
